import { useState, useEffect } from "react";
import {
  ExternalLink,
  Crown,
  Users,
  Clock,
  Search,
  Eye,
  ChevronUp,
  ChevronDown,
  User,
  Briefcase,
  X,
  ShieldCheck,
  Settings,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useToast } from "../../components/ui/toast";
import { AdminUser, ListParams, UserPermissions } from "../../types/admin";
import { listUsers, updateUserPermissions } from "../../lib/data/users";
import { formatDateTime, isoToDate } from "../../lib/format";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Switch } from "../../components/ui/switch";

interface PermissionDef {
  key: keyof UserPermissions;
  label: string;
  description: string;
  icon: React.ReactNode;
}

const PERMISSION_DEFS: PermissionDef[] = [
  {
    key: "view_all_projects",
    label: "View All Projects",
    description: "Can open and view any user's projects without restriction",
    icon: <Eye className="h-4 w-4 text-blue-500" />,
  },
  {
    key: "edit_all_projects",
    label: "Edit All Projects",
    description: "Can edit and modify any user's projects",
    icon: <Settings className="h-4 w-4 text-orange-500" />,
  },
  {
    key: "manage_users",
    label: "Manage Users",
    description:
      "Can change subscriptions, roles and permissions of other users",
    icon: <ShieldCheck className="h-4 w-4 text-purple-500" />,
  },
];

export default function AdminUsersPage() {
  const { toast } = useToast();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [loading, setLoading] = useState(false);
  const [planFilter, setPlanFilter] = useState<string>("all");

  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [dealsDialogOpen, setDealsDialogOpen] = useState(false);

  const [permUser, setPermUser] = useState<AdminUser | null>(null);
  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [pendingPerms, setPendingPerms] = useState<UserPermissions>({});
  const [savingPerms, setSavingPerms] = useState(false);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params: ListParams = {
        search: search || undefined,
        sort,
        dir: sortDir,
        page,
        pageSize,
      };

      const result = await listUsers(params);
      setUsers(result.rows);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [page, pageSize, search, sort, sortDir]);

  const handleSort = (column: string) => {
    if (sort === column) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSort(column);
      setSortDir("asc");
    }
    setPage(1);
  };

  const handleSearch = (newSearch: string) => {
    setSearch(newSearch);
    setPage(1);
  };

  const handleViewDeals = (user: AdminUser) => {
    setSelectedUser(user);
    setDealsDialogOpen(true);
  };

  const handleOpenPermissions = (user: AdminUser) => {
    setPermUser(user);
    setPendingPerms({ ...(user.permissions || {}) });
    setPermDialogOpen(true);
  };

  const handleSavePermissions = async () => {
    if (!permUser) return;
    setSavingPerms(true);
    try {
      const updated = await updateUserPermissions(permUser.id, pendingPerms);
      setUsers((prev) =>
        prev.map((u) =>
          u.id === updated.id ? { ...u, permissions: updated.permissions } : u,
        ),
      );
      toast.success("Permissions updated successfully");
      setPermDialogOpen(false);
    } catch {
      toast.error("Failed to update permissions");
    } finally {
      setSavingPerms(false);
    }
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      user.email?.toLowerCase().includes(search.toLowerCase());
    const matchesPlan =
      planFilter === "all" ||
      (planFilter === "free" && user.subscription === "free") ||
      (planFilter === "pro" && user.subscription === "pro") ||
      (planFilter === "enterprise" && user.subscription === "enterprise");
    return matchesSearch && matchesPlan;
  });

  const totalUsers = users.length;
  const proUsers = users.filter((u) => u.subscription === "pro").length;
  const enterpriseUsers = users.filter(
    (u) => u.subscription === "enterprise",
  ).length;
  const totalDeals = users.reduce((sum, u) => sum + (u.dealsCount || 0), 0);

  const getPlanBadge = (subscription: string) => {
    switch (subscription) {
      case "enterprise":
        return (
          <Badge className="bg-purple-500/10 text-purple-600 border-purple-200">
            Enterprise
          </Badge>
        );
      case "pro":
        return (
          <Badge className="bg-blue-500/10 text-blue-600 border-blue-200">
            Pro
          </Badge>
        );
      default:
        return <Badge variant="secondary">Free</Badge>;
    }
  };

  const hasAnyPermission = (u: AdminUser) =>
    Object.values(u.permissions || {}).some(Boolean);

  const getSortIcon = (columnKey: string) => {
    if (sort !== columnKey) return null;
    return sortDir === "asc" ? (
      <ChevronUp className="h-4 w-4" />
    ) : (
      <ChevronDown className="h-4 w-4" />
    );
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisible = 5;

    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      buttons.push(
        <Button
          key={i}
          variant={i === page ? "default" : "outline"}
          size="sm"
          onClick={() => setPage(i)}
          className="w-10"
        >
          {i}
        </Button>,
      );
    }

    return buttons;
  };

  const renderSkeletonRows = () => {
    return Array.from({ length: 8 }).map((_, rowIndex) => (
      <TableRow key={rowIndex} className="border-b border-slate-100">
        <TableCell>
          <div className="flex justify-start">
            <Skeleton className="h-4 w-48" />
          </div>
        </TableCell>
        <TableCell>
          <div className="flex justify-center">
            <Skeleton className="h-4 w-20" />
          </div>
        </TableCell>
        <TableCell>
          <div className="flex justify-center">
            <Skeleton className="h-4 w-24" />
          </div>
        </TableCell>
        <TableCell>
          <div className="flex justify-center">
            <Skeleton className="h-4 w-32" />
          </div>
        </TableCell>
        <TableCell className="text-center">
          <div className="flex justify-center">
            <Skeleton className="h-4 w-12" />
          </div>
        </TableCell>
        <TableCell className="text-right">
          <div className="flex justify-end gap-2">
            <Skeleton className="h-8 w-24" />
            <Skeleton className="h-8 w-24" />
          </div>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Users</h1>
        <p className="text-muted-foreground">
          Manage platform users and view their activity
        </p>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalUsers}</div>
            <p className="text-xs text-muted-foreground">
              +{Math.max(0, users.length - 5)} this week
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Pro Users</CardTitle>
            <Crown className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{proUsers}</div>
            <p className="text-xs text-muted-foreground">
              {totalUsers > 0 ? Math.round((proUsers / totalUsers) * 100) : 0}%
              of total
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Enterprise</CardTitle>
            <Crown className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{enterpriseUsers}</div>
            <p className="text-xs text-muted-foreground">
              {totalUsers > 0
                ? Math.round((enterpriseUsers / totalUsers) * 100)
                : 0}
              % of total
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Deals</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalDeals}</div>
            <p className="text-xs text-muted-foreground">
              {totalUsers > 0 ? (totalDeals / totalUsers).toFixed(1) : 0} avg
              per user
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={planFilter} onValueChange={setPlanFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Filter by plan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="pro">Pro</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-slate-600">Rows per page:</span>
              <Select
                value={pageSize.toString()}
                onValueChange={(v) => {
                  setPageSize(Number(v));
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead
                  className="cursor-pointer hover:bg-slate-100 transition duration-150"
                  onClick={() => handleSort("full_name")}
                >
                  <div className="flex items-center space-x-1">
                    <span>User</span>
                    {getSortIcon("full_name")}
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-slate-100 transition duration-150"
                  onClick={() => handleSort("subscription")}
                >
                  <div className="flex items-center justify-center space-x-1">
                    <span>Plan</span>
                    {getSortIcon("subscription")}
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-slate-100 transition duration-150"
                  onClick={() => handleSort("createdAt")}
                >
                  <div className="flex items-center justify-center space-x-1">
                    <span>Registered</span>
                    {getSortIcon("createdAt")}
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-slate-100 transition duration-150"
                  onClick={() => handleSort("last_login")}
                >
                  <div className="flex items-center justify-center space-x-1">
                    <span>Last Login</span>
                    {getSortIcon("last_login")}
                  </div>
                </TableHead>
                <TableHead className="text-center">Deals</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                renderSkeletonRows()
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="py-12 text-center text-slate-500 text-base"
                  >
                    No users found.
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow
                    key={user.id}
                    className="hover:bg-slate-50/50 transition-colors"
                  >
                    <TableCell>
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-medium">
                            {user.full_name || "Unknown"}
                          </span>
                          {user.role === "admin" && (
                            <Crown
                              className="h-4 w-4 text-yellow-500"
                              aria-label="Administrator"
                            />
                          )}
                          {hasAnyPermission(user) && (
                            <ShieldCheck
                              className="h-4 w-4 text-emerald-500"
                              aria-label="Has custom permissions"
                              title="Has custom permissions"
                            />
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {user.email}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {getPlanBadge(user.subscription)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-center">
                      {isoToDate(user.createdAt)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-center">
                      {formatDateTime(user.last_login)}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">{user.dealsCount || 0}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewDeals(user)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View Deals
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenPermissions(user)}
                          className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                        >
                          <ShieldCheck className="h-4 w-4 mr-1" />
                          Permissions
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-6 border-t border-slate-200 mt-6">
              <div className="text-sm text-slate-600">
                Showing{" "}
                <span className="font-semibold text-slate-800">
                  {(page - 1) * pageSize + 1}
                </span>{" "}
                to{" "}
                <span className="font-semibold text-slate-800">
                  {Math.min(page * pageSize, total)}
                </span>{" "}
                of <span className="font-semibold text-slate-800">{total}</span>{" "}
                results
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                  className="px-3"
                >
                  Previous
                </Button>

                <div className="flex space-x-1">
                  {renderPaginationButtons()}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages}
                  className="px-3"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dealsDialogOpen} onOpenChange={setDealsDialogOpen}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden rounded-2xl">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle className="text-xl font-semibold flex items-center gap-2">
              <User className="h-5 w-5" />
              {selectedUser?.full_name}'s Deals
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground mt-1">
              View and manage user's deal portfolio
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 py-6">
            {selectedUser?.dealsCount ? (
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-800">
                        Total Deals
                      </p>
                      <div className="flex items-baseline gap-2 mt-1">
                        <span className="text-3xl font-bold text-blue-900">
                          {selectedUser.dealsCount}
                        </span>
                        <span className="text-sm text-blue-600">
                          {selectedUser.dealsCount === 1 ? "deal" : "deals"}
                        </span>
                      </div>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-white/80 border border-blue-300 flex items-center justify-center">
                      <Briefcase className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    This user has created {selectedUser.dealsCount} deal
                    {selectedUser.dealsCount !== 1 ? "s" : ""}. You can view and
                    manage all their deals in the admin panel.
                  </p>

                  <div className="flex gap-3 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => setDealsDialogOpen(false)}
                      className="flex-1 h-11"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Close
                    </Button>
                    <Button
                      onClick={() => {
                        window.open(
                          `/admin/users/projects?userId=${selectedUser.id}`,
                          "_blank",
                          "noopener,noreferrer",
                        );
                      }}
                      className="flex-1 h-11 bg-primary-blue"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View User Projects
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center space-y-6 py-4">
                <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto">
                  <Briefcase className="h-10 w-10 text-gray-400" />
                </div>

                <div className="space-y-2">
                  <h3 className="text-lg font-medium">No deals yet</h3>
                  <p className="text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
                    This user hasn't created any deals yet. Deals will appear
                    here once they start using the platform.
                  </p>
                </div>

                <div className="pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setDealsDialogOpen(false)}
                    className="h-11 px-6"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Close
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={permDialogOpen} onOpenChange={setPermDialogOpen}>
        <DialogContent className="max-w-lg p-0 overflow-hidden rounded-2xl">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle className="text-xl font-semibold flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
              Permissions — {permUser?.full_name}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground mt-1">
              {permUser?.email} · Toggle permissions and save.
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 py-5 space-y-4">
            {PERMISSION_DEFS.map((def) => (
              <div
                key={def.key}
                className="flex items-start justify-between gap-4 p-4 rounded-xl border border-slate-100 bg-slate-50/60 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">{def.icon}</div>
                  <div>
                    <p className="text-sm font-medium leading-tight">
                      {def.label}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {def.description}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={!!pendingPerms[def.key]}
                  onCheckedChange={(checked) =>
                    setPendingPerms((prev) => ({ ...prev, [def.key]: checked }))
                  }
                />
              </div>
            ))}
          </div>

          <div className="px-6 pb-6 flex gap-3">
            <Button
              variant="outline"
              className="flex-1 h-11"
              onClick={() => setPermDialogOpen(false)}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              className="flex-1 h-11 bg-primary-blue"
              onClick={handleSavePermissions}
              disabled={savingPerms}
            >
              {savingPerms ? "Saving…" : "Save permissions"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
